import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQuery } from '@apollo/client';
import { GET_WHERE_TO_GO_CATEGORIES } from '../graphql/whereToGo';
import ImageUploader from './ImageUploader';
import MultiSelectArea from './MultiSelectArea';
import MultiSelectCategory from './MultiSelectCategory';

// Define the form validation schema
const whereToGoSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title is too long'),
  description: z.string().optional(),
  address: z.string().max(200, 'Address is too long').optional().or(z.literal('')),
  city: z.string().max(100, 'City is too long').optional().or(z.literal('')),
  state: z.string().max(50, 'State is too long').optional().or(z.literal('')),
  zipCode: z.string().max(20, 'Zip code is too long').optional().or(z.literal('')),
  phoneNumber: z.string().optional(),
  website: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  tips: z.string().optional(),
  promotionBannerText: z.string().max(150, 'Banner text is too long').optional().or(z.literal('')),
  promotionDetails: z.string().max(2000, 'Promotion details too long').optional().or(z.literal('')),
  promotionColor: z.string().max(7).optional().or(z.literal('')),
  categoryIds: z.array(z.string()).optional(),
  imageUrl: z.string().optional(),
  areas: z.array(z.string()).optional(),
  isActive: z.boolean().default(true)
});

const PROMOTION_COLOR_PRESETS = [
  { hex: '#3B4559', label: 'Slate' },
  { hex: '#315470', label: 'Deep Blue' },
  { hex: '#4D7D5C', label: 'Kiawah Green' },
  { hex: '#9E575E', label: 'Seabrook Rose' },
  { hex: '#3B6175', label: 'Isle Blue' },
  { hex: '#2B1C3D', label: 'Wild Dunes Purple' },
  { hex: '#1B5E20', label: 'Forest' },
  { hex: '#B71C1C', label: 'Deep Red' },
  { hex: '#E65100', label: 'Burnt Orange' },
  { hex: '#004D40', label: 'Teal' },
  { hex: '#1A237E', label: 'Navy' },
  { hex: '#880E4F', label: 'Burgundy' },
];

export default function WhereToGoForm({ initialData, onSubmit, isLoading }) {
  // Keep state of image URL
  const [imageUrl, setImageUrl] = useState(initialData?.imageUrl || '');

  // Fetch categories
  const { data: categoriesData, loading: loadingCategories } = useQuery(GET_WHERE_TO_GO_CATEGORIES);
  const categories = categoriesData?.whereToGoCategories || [];

  // Set up form with react-hook-form and zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    control
  } = useForm({
    resolver: zodResolver(whereToGoSchema),
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      address: initialData?.address || '',
      city: initialData?.city || '',
      state: initialData?.state || '',
      zipCode: initialData?.zipCode || '',
      phoneNumber: initialData?.phoneNumber || '',
      website: initialData?.website || '',
      tips: initialData?.tips || '',
      promotionBannerText: initialData?.promotionBannerText || '',
      promotionDetails: initialData?.promotionDetails || '',
      promotionColor: initialData?.promotionColor || '',
      categoryIds: initialData?.categories?.map(c => c.id) || [],
      imageUrl: initialData?.imageUrl || '',
      areas: initialData?.areas || [],
      isActive: initialData?.isActive ?? true
    }
  });

  // Handle image upload completion
  const handleImageUploaded = (url) => {
    setImageUrl(url);
    setValue('imageUrl', url);
  };

  // Handle form submission
  const handleFormSubmit = async (data) => {
    try {
      const cleaned = {
        title: data.title,
        description: data.description || null,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        zipCode: data.zipCode || null,
        phoneNumber: data.phoneNumber || null,
        website: data.website || null,
        tips: data.tips || null,
        promotionBannerText: data.promotionBannerText || null,
        promotionDetails: data.promotionDetails || null,
        promotionColor: data.promotionColor || null,
        categoryIds: Array.isArray(data.categoryIds) ? data.categoryIds : [],
        imageUrl: imageUrl || null,
        areas: Array.isArray(data.areas) ? data.areas : [],
        isActive: data.isActive,
      };
      await onSubmit(cleaned);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {initialData ? 'Edit Location' : 'Add New Location'}
          </h2>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
          <div className="space-y-4">
            {/* Title Field */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Location Name <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                {...register('title')}
                className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                aria-invalid={errors.title ? 'true' : 'false'}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.title.message}</p>
              )}
            </div>

            {/* Category Selection (Multi-select) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories
              </label>
              <Controller
                name="categoryIds"
                control={control}
                render={({ field }) => (
                  <MultiSelectCategory
                    categories={categories}
                    value={field.value || []}
                    onChange={field.onChange}
                    isLoading={loadingCategories}
                    error={errors.categoryIds}
                  />
                )}
              />
              {errors.categoryIds && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.categoryIds.message}</p>
              )}
            </div>

            {/* Area Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Areas
              </label>
              <Controller
                name="areas"
                control={control}
                render={({ field }) => (
                  <MultiSelectArea
                    value={field.value || []}
                    onChange={field.onChange}
                    error={errors.areas}
                  />
                )}
              />
              {errors.areas && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.areas.message}</p>
              )}
            </div>

            {/* Image Upload Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Image
              </label>
              <ImageUploader
                initialImage={initialData?.imageUrl}
                onImageUploaded={handleImageUploaded}
              />
            </div>

            {/* Description Field */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                {...register('description')}
                className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.description.message}</p>
              )}
            </div>

            {/* Address Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  id="address"
                  type="text"
                  {...register('address')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                  aria-invalid={errors.address ? 'true' : 'false'}
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.address.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  id="city"
                  type="text"
                  {...register('city')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                  aria-invalid={errors.city ? 'true' : 'false'}
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.city.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  id="state"
                  type="text"
                  {...register('state')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                  aria-invalid={errors.state ? 'true' : 'false'}
                />
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.state.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Zip Code
                </label>
                <input
                  id="zipCode"
                  type="text"
                  {...register('zipCode')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                  aria-invalid={errors.zipCode ? 'true' : 'false'}
                />
                {errors.zipCode && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.zipCode.message}</p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  id="phoneNumber"
                  type="text"
                  {...register('phoneNumber')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                />
                {errors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.phoneNumber.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  id="website"
                  type="text"
                  placeholder="https://"
                  {...register('website')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.website.message}</p>
                )}
              </div>
            </div>

            {/* Tips Field */}
            <div>
              <label htmlFor="tips" className="block text-sm font-medium text-gray-700 mb-1">
                Tips & Information
              </label>
              <textarea
                id="tips"
                rows={4}
                {...register('tips')}
                className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
              />
              {errors.tips && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.tips.message}</p>
              )}
            </div>

            {/* Promotion Section */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
              <h3 className="text-md font-semibold text-gray-800">Promotion</h3>

              <div>
                <label htmlFor="promotionBannerText" className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion Banner Display Text
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  This text displays over the image to show the promotion
                </p>
                <input
                  id="promotionBannerText"
                  type="text"
                  maxLength={150}
                  placeholder="e.g. 10% Off with code BEACH10"
                  {...register('promotionBannerText')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                />
                {errors.promotionBannerText && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.promotionBannerText.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="promotionDetails" className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion Details
                </label>
                <textarea
                  id="promotionDetails"
                  rows={4}
                  placeholder="Describe the promotion details, codes, or instructions for the guest..."
                  {...register('promotionDetails')}
                  className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                />
                {errors.promotionDetails && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.promotionDetails.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion Banner Color
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select a background color for the promotion banner (white text will overlay)
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {PROMOTION_COLOR_PRESETS.map((color) => (
                    <button
                      key={color.hex}
                      type="button"
                      onClick={() => setValue('promotionColor', color.hex)}
                      className={`w-9 h-9 rounded-lg border-2 transition-all ${
                        watch('promotionColor') === color.hex
                          ? 'border-sky-500 ring-2 ring-sky-300 scale-110'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.label}
                      aria-label={`Select ${color.label} color`}
                    />
                  ))}
                  {watch('promotionColor') && (
                    <button
                      type="button"
                      onClick={() => setValue('promotionColor', '')}
                      className="h-9 px-2 rounded-lg border border-gray-300 text-xs text-gray-500 hover:bg-gray-100"
                      aria-label="Clear color selection"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="#3B4559"
                    maxLength={7}
                    value={watch('promotionColor') || ''}
                    onChange={(e) => setValue('promotionColor', e.target.value)}
                    className="w-28 rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2 text-sm"
                  />
                  {watch('promotionColor') && (
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded border border-gray-300"
                        style={{ backgroundColor: watch('promotionColor') }}
                      />
                      <span
                        className="text-sm font-medium px-3 py-1 rounded"
                        style={{
                          backgroundColor: watch('promotionColor'),
                          color: '#ffffff',
                        }}
                      >
                        Preview
                      </span>
                    </div>
                  )}
                </div>
                {errors.promotionColor && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.promotionColor.message}</p>
                )}
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                id="isActive"
                type="checkbox"
                {...register('isActive')}
                className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                Active
              </label>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="submit"
              className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? 'Saving...' : (initialData ? 'Update Location' : 'Add Location')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
